import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SortableIndicator } from '~/modules/label-perf/components/Table/SortableIndicator';
import type { TrackPerformanceItem } from '~/modules/label-perf/types';
import { formatNumberSuffix } from '~/modules/label-perf/utils/format';

import { ArtistLink } from './components/ArtistLink';
import { MajorLabels } from './components/MajorLabels';
import { PerformanceDeltaLabel } from './components/PerformanceDeltaLabel';
import { TimeRangeLabel } from './components/TimeRangeLabel';
import { getVintage } from './utils';

export function useTrackPerformanceTableColumns(
  streamFilter: 'total' | 'platform',
) {
  const { t } = useTranslation('label-perf');

  return useMemo(() => {
    const columnHelper = createColumnHelper<TrackPerformanceItem>();

    //  BASE COLUMNS (Always Visible)
    const baseColumns = [
      columnHelper.accessor((_, index) => index, {
        id: 'rowNumber',
        meta: { className: 'w-[90px]' },
        enableSorting: true,
        header: () => (
          <div className="flex items-center gap-sm pl-md">
            <span>#</span>
          </div>
        ),
        cell: (info) => {
          // Find the row's index within the currently visible page rows.
          // const visualIndex =
          //   info.table
          //     .getPaginationRowModel()
          //     .rows.findIndex((row) => row.id === info.row.id) + 1;

          const visualIndex = info.row.index + 1; // On a next page the index should continue

          return <div className="p-sm pl-md font-semibold">{visualIndex}</div>;
        },
      }),
      columnHelper.accessor('familyTitle', {
        id: 'familyTitle',
        enableSorting: true,
        meta: { className: 'w-[280px]' },
        header: () => (
          <div className="flex items-center gap-sm">
            <span>{t('table.trackTitle')}</span>
          </div>
        ),
        cell: ({ row }) => {
          const { familyTitle, artist, relatedArtists } = row.original;

          return (
            <div className="max-w-[16em] p-sm font-semibold">
              <p className="truncate">{familyTitle || '--'}</p>

              <ArtistLink artist={artist} relatedArtists={relatedArtists} />
            </div>
          );
        },
      }),
      columnHelper.accessor('majorLabels', {
        id: 'majorLabels',
        enableSorting: true,
        meta: {
          className: 'w-[190px]',
        },
        header: () => (
          <div className="flex items-center gap-sm">
            <span>{t('table.labels')}</span>
          </div>
        ),
        cell: ({ row }) => {
          const majorLabels = row.original.majorLabels || [];

          return <MajorLabels labels={majorLabels} />;
        },
      }),
      columnHelper.accessor((row) => getVintage(row.releaseDate), {
        id: 'vintages',
        header: () => (
          <div className="flex items-center gap-sm">
            <span>{t('table.vintages')}</span>
          </div>
        ),
        enableSorting: true,
        meta: {
          className: 'w-[150px]',
        },
        cell: ({ getValue }) => (
          <div className="p-sm">{t(`table.${getValue()}`)}</div>
        ),
      }),
    ];

    // PLATFORM STREAMS VIEW
    if (streamFilter === 'platform') {
      return [
        ...baseColumns,
        columnHelper.group({
          id: 'adSupportedPaidGroup',
          meta: {
            className: 'w-[190px]',
          },
          header: () => '',
          columns: [
            columnHelper.accessor('pctStreamsAdSupported', {
              header: () => 'Ad %',
              meta: { className: 'border-l border-l-emphasis-strong w-[95px]' },
              cell: (info) => info.getValue<number>()?.toFixed(2) || '--',
            }),

            columnHelper.accessor('pctStreamsPaid', {
              header: () => 'Paid %',
              meta: { className: 'border-r border-r-emphasis-strong w-[95px]' },
              cell: (info) => info.getValue<number>()?.toFixed(2) || '--',
            }),
          ],
        }),
        columnHelper.group({
          id: 'platformStreamsGroup',
          header: () => '',
          meta: {
            className: 'w-[475px]',
          },
          columns: [
            columnHelper.accessor('pctStreamsAmazon', {
              header: () => 'Amazon',
              meta: {
                className: 'border-r border-r-emphasis-strong w-[95px]',
              },
              cell: (info) => info.getValue<number>()?.toFixed(2) || '--',
            }),
            columnHelper.accessor('pctStreamsApple', {
              header: () => 'Apple',
              meta: {
                className: 'border-r border-r-emphasis-strong w-[95px]',
              },
              cell: (info) => info.getValue<number>()?.toFixed(2) || '--',
            }),
            columnHelper.accessor('pctStreamsSpotify', {
              header: () => 'Spotify',
              meta: {
                className: 'border-r border-r-emphasis-strong w-[95px]',
              },
              cell: (info) => info.getValue<number>()?.toFixed(2) || '--',
            }),
            columnHelper.accessor('pctStreamsYoutube', {
              header: () => 'Youtube',
              meta: {
                className: 'border-r border-r-emphasis-strong w-[95px]',
              },
              cell: (info) => info.getValue<number>()?.toFixed(2) || '--',
            }),
            columnHelper.accessor('pctStreamsOther', {
              header: () => 'Other',
              meta: {
                className: 'w-[95px]',
              },
              cell: (info) => info.getValue<number>()?.toFixed(2) || '--',
            }),
          ],
        }),
      ];
    }

    return [
      ...baseColumns,
      columnHelper.accessor('streamsRtd', {
        id: 'streamsRtd',
        enableSorting: true,
        meta: { className: 'w-[162px]' },
        header: (header) => (
          <div className="flex items-end gap-xs">
            <span className="text-body-sm">{t('table.rtdTwStreams')}</span>
            <TimeRangeLabel timeRange="RTD" />
            <SortableIndicator
              column={header.column}
              tooltip={t('tooltips.sortBy', { field: t('table.rtdTwStreams') })}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="p-sm">
            <p>{formatNumberSuffix(row.original.streamsRtd)}</p>
          </div>
        ),
      }),
      columnHelper.accessor('streamsTw', {
        id: 'streamsTw',
        enableSorting: true,
        meta: { className: 'w-[142px]' },
        header: (header) => (
          <div className="flex items-end gap-xs">
            <span className="text-body-sm">{t('table.rtdTwStreams')}</span>
            <TimeRangeLabel timeRange="TW" />
            <SortableIndicator
              column={header.column}
              tooltip={t('tooltips.sortBy', { field: t('table.rtdTwStreams') })}
            />
          </div>
        ),
        cell: ({ row }) => {
          return (
            <div className="flex flex-col px-sm">
              <span>{formatNumberSuffix(row.original.streamsTw) || '--'}</span>
              <PerformanceDeltaLabel delta={row.original.streamsWow} />
            </div>
          );
        },
      }),
      columnHelper.accessor('organicStreamsTw', {
        id: 'organicStreamsTw',
        enableSorting: true,
        meta: { className: 'w-[172px]' },
        header: () => (
          <div className="flex items-end gap-xs">
            <span className="text-body-sm">{t('table.organicStreams')}</span>
            <TimeRangeLabel timeRange="TW" />
          </div>
        ),
        cell: ({ row }) => {
          return (
            <div className="flex flex-col px-sm">
              <span>
                {formatNumberSuffix(row.original.organicStreamsTw) || '--'}
              </span>
              <PerformanceDeltaLabel delta={row.original.organicStreamsWow} />
            </div>
          );
        },
      }),
      columnHelper.accessor('socialCreationsTw', {
        id: 'socialCreationsTw',
        enableSorting: true,
        meta: { className: 'w-[172px]' },
        header: () => (
          <div className="flex items-end gap-xs">
            <span className="text-body-sm">{t('table.socialCreations')}</span>
            <TimeRangeLabel timeRange="TW" />
          </div>
        ),
        cell: ({ row }) => {
          return (
            <div className="flex flex-col px-sm">
              <span>
                {formatNumberSuffix(row.original.socialCreationsTw) || '--'}
              </span>
              <PerformanceDeltaLabel delta={row.original.socialCreationsWow} />
            </div>
          );
        },
      }),
    ];
  }, [t, streamFilter]);
}
